import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import Sidebar from '../components/Sidebar.jsx';
import Modal from '../components/Modal.jsx';
import TagPill, { TAG_PALETTE } from '../components/TagPill.jsx';
import Campaigns from './Campaigns.jsx';
import Content   from './Content.jsx';
import Forms     from './Forms.jsx';
import Reports   from './Reports.jsx';
import Inbox     from './Inbox.jsx';
import { fmtIST } from '../lib/datetime.js';

export default function Admin() {
  const [active, setActive] = useState('contacts');
  const [pendingCampaignId, setPendingCampaignId] = useState(null);
  const [stats, setStats]   = useState(null);
  const [health, setHealth] = useState(null);
  const [tags, setTags]     = useState([]);
  const [activity, setActivity] = useState({ sends: [], events: [] });

  // Contacts state
  const [q, setQ] = useState('');
  const [phoneOnly, setPhoneOnly] = useState(false);
  const [tagFilter, setTagFilter] = useState('');
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [offset, setOffset]     = useState(0);
  const [busy, setBusy]         = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  // Send actions
  const [waTemplate, setWaTemplate] = useState(() => localStorage.getItem('iaapi_wa_template') || '');
  const [liveMode, setLiveMode] = useState(false);
  const [linkFor, setLinkFor]   = useState(null);
  const [sendFor, setSendFor]   = useState(null);

  // Import + modal state
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const fileRef = useRef(null);

  const limit = 25;

  /* ---------------- data loaders ---------------- */
  async function loadStats()   { try { setStats(await api.get('/api/contacts/stats')); } catch {} }
  async function loadHealth()  { try { setHealth(await api.get('/health')); } catch {} }
  async function loadTags()    { try { const r = await api.get('/api/tags'); setTags(r.rows || []); } catch {} }
  async function loadActivity(){ try { setActivity(await api.get('/api/activity/recent?limit=40')); } catch {} }

  async function loadRows(off = 0, query = q, valid = phoneOnly, tagSlug = tagFilter) {
    setBusy(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(off) });
      if (query)   params.set('q', query);
      if (tagSlug) params.set('tag', tagSlug);
      const r = await api.get(`/api/contacts?${params}`);
      let filtered = r.rows;
      if (valid) filtered = filtered.filter(x => x.phone_valid);
      setRows(filtered);
      setTotal(r.total);
      setOffset(off);
    } finally { setBusy(false); }
  }

  useEffect(() => { loadStats(); loadHealth(); loadRows(0); loadActivity(); loadTags(); /* eslint-disable-line */ }, []);
  useEffect(() => {
    const t = setInterval(loadActivity, 5000);
    return () => clearInterval(t);
  }, []);

  /* ---------------- selection ---------------- */
  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function selectAllOnPage() {
    setSelected(prev => {
      const next = new Set(prev);
      for (const r of rows) next.add(r.id);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  /* ---------------- live mode ---------------- */
  function toggleLiveMode(e) {
    const next = e.target.checked;
    if (next && !window.confirm('Live mode: clicking WA / Email below will send REAL messages. Continue?')) {
      e.target.checked = false;
      return;
    }
    setLiveMode(next);
  }
  function onWaTemplateChange(e) {
    const v = e.target.value;
    setWaTemplate(v);
    localStorage.setItem('iaapi_wa_template', v);
  }

  /* ---------------- per-row send / link ---------------- */
  async function getJoinLink(id) {
    setLinkFor({ id, loading: true });
    try {
      const r = await api.post(`/api/contacts/${id}/token`);
      setLinkFor({ id, url: r.url });
      navigator.clipboard?.writeText(r.url).catch(() => {});
    } catch (e) { setLinkFor({ id, error: e.message }); }
  }

  async function deleteContact(id, name, source) {
    const verb = source === 'test_recipient' ? 'Delete this test recipient' : `Delete contact "${name}"`;
    if (!window.confirm(`${verb}? Their tags, tokens, sends, and events go too. Form submissions stay (just lose the contact link).`)) return;
    try {
      await api.del(`/api/contacts/${id}`);
      await Promise.all([loadStats(), loadRows(offset, q, phoneOnly, tagFilter), loadTags()]);
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) { window.alert('Delete failed: ' + (e.body?.error || e.message)); }
  }

  async function testSend(id, channel) {
    if (channel === 'wa' && !waTemplate.trim()) {
      window.alert('Set the WA template name above the table first.');
      return;
    }
    if (liveMode) {
      const row = rows.find(r => r.id === id);
      const dest = channel === 'wa' ? `+${row?.phone || ''}` : (row?.email || '');
      if (!window.confirm(`Send a REAL ${channel === 'wa' ? 'WhatsApp message' : 'email'} to ${dest}?`)) return;
    }
    setSendFor({ id, channel, loading: true });
    try {
      const body = { channel, live: liveMode };
      if (channel === 'wa') body.template = waTemplate.trim();
      const r = await api.post(`/api/contacts/${id}/send-test`, body);
      setSendFor({ id, channel, result: r });
      loadActivity();
    } catch (e) {
      setSendFor({ id, channel, error: e.body?.error || e.message });
      loadActivity();
    }
  }

  /* ---------------- bulk tag actions ---------------- */
  async function bulkAddTag(tagId) {
    if (!tagId || !selected.size) return;
    try {
      await api.post('/api/tags/bulk-apply', { contact_ids: [...selected], add: [tagId] });
      await Promise.all([loadTags(), loadRows(offset, q, phoneOnly, tagFilter)]);
    } catch (e) { window.alert('Add tag failed: ' + (e.body?.error || e.message)); }
  }
  async function bulkRemoveTag(tagId) {
    if (!tagId || !selected.size) return;
    try {
      await api.post('/api/tags/bulk-apply', { contact_ids: [...selected], remove: [tagId] });
      await Promise.all([loadTags(), loadRows(offset, q, phoneOnly, tagFilter)]);
    } catch (e) { window.alert('Remove tag failed: ' + (e.body?.error || e.message)); }
  }

  /* ---------------- import ---------------- */
  async function handleImport(e) {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setImporting(true); setImportMsg('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('source', 'expo2026_visitors');
      const r = await api.post('/api/import/contacts', fd);
      setImportMsg(`Inserted ${r.inserted ?? 0}, updated ${r.updated ?? 0}, invalid phone ${r.invalid_phone ?? 0}, dupes ${r.duplicate_in_file ?? 0}.`);
      await Promise.all([loadStats(), loadRows(0)]);
    } catch (e) { setImportMsg('Failed: ' + e.message); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  return (
    <div className="min-h-screen flex bg-slate-50">
      <Sidebar active={active} onNavigate={setActive} sendMode={health?.send_mode} />

      <main className="flex-1 min-w-0">
        {active === 'contacts' && (
          <ContactsPage
            stats={stats}
            tags={tags}
            rows={rows}
            total={total}
            offset={offset}
            limit={limit}
            busy={busy}
            q={q} setQ={setQ}
            phoneOnly={phoneOnly} setPhoneOnly={setPhoneOnly}
            tagFilter={tagFilter} setTagFilter={setTagFilter}
            selected={selected}
            toggleSelect={toggleSelect}
            selectAllOnPage={selectAllOnPage}
            clearSelection={clearSelection}
            loadRows={loadRows}
            waTemplate={waTemplate} onWaTemplateChange={onWaTemplateChange}
            liveMode={liveMode} toggleLiveMode={toggleLiveMode}
            linkFor={linkFor} sendFor={sendFor}
            getJoinLink={getJoinLink} testSend={testSend}
            deleteContact={deleteContact}
            bulkAddTag={bulkAddTag} bulkRemoveTag={bulkRemoveTag}
            onOpenImport={() => setImportOpen(true)}
            activity={activity}
            onLoadActivity={loadActivity}
          />
        )}

        {active === 'tags'      && <TagsPage tags={tags} onChange={loadTags} />}
        {active === 'campaigns' && <Campaigns tags={tags} initialOpenId={pendingCampaignId} onOpened={() => setPendingCampaignId(null)} />}
        {active === 'content'   && <Content />}
        {active === 'forms'     && <Forms />}
        {active === 'reports'   && <Reports onOpenCampaign={(id) => { setPendingCampaignId(id); setActive('campaigns'); }} />}
        {active === 'inbox'     && <Inbox />}
      </main>

      <Modal open={importOpen} onClose={() => setImportOpen(false)} title="Import contacts (CSV)"
        footer={
          <>
            <button className="btn-ghost" onClick={() => setImportOpen(false)}>Close</button>
            <button className="btn" onClick={(e) => handleImport(e)} disabled={importing}>{importing ? 'Importing…' : 'Import'}</button>
          </>
        }>
        <p className="text-sm text-slate-600 mb-3">CSV from IAAPI registration. Source tag: <code className="px-1 bg-slate-100 rounded">expo2026_visitors</code>. Re-imports dedupe by phone.</p>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="block text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100" />
        {importMsg && <div className="mt-3 text-sm text-slate-600">{importMsg}</div>}
      </Modal>

      <Modal open={tagModalOpen} onClose={() => setTagModalOpen(false)} title="Manage tags" maxWidth="max-w-2xl">
        <TagManagerInner tags={tags} onChange={loadTags} />
      </Modal>
    </div>
  );
}

/* ============================================================================
 * CONTACTS PAGE
 * ============================================================================ */
function ContactsPage(props) {
  const {
    stats, tags, rows, total, offset, limit, busy,
    q, setQ, phoneOnly, setPhoneOnly, tagFilter, setTagFilter,
    selected, toggleSelect, selectAllOnPage, clearSelection,
    loadRows,
    waTemplate, onWaTemplateChange, liveMode, toggleLiveMode,
    linkFor, sendFor, getJoinLink, testSend,
    deleteContact,
    bulkAddTag, bulkRemoveTag,
    onOpenImport,
    activity, onLoadActivity,
  } = props;

  const allChecked = rows.length > 0 && rows.every(r => selected.has(r.id));

  return (
    <div className="px-8 py-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Contacts</h1>
          <p className="text-sm text-slate-500 mt-0.5">Tag your audience, run quick tests, and prep for campaigns.</p>
        </div>
        <button className="btn-ghost" onClick={onOpenImport}>Import CSV</button>
      </div>

      {/* stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total contacts" value={stats?.total ?? '…'} />
        <StatCard label="Tags" value={tags.length}
                  hint={tags.slice(0, 4).map(t => t.name).join(' · ') + (tags.length > 4 ? '…' : '')} />
        <StatCard label="Tagged"
                  value={stats?.total ?? '…'}
                  hint={(tags.find(t => t.slug === 'src-expo2026-visitors')?.contact_count ?? 0).toLocaleString() + ' from expo'}/>
      </div>

      {/* filter + actions card */}
      <div className="card p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <input className="input w-full pl-9" placeholder="Search name, phone, email, organisation…"
              value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') loadRows(0, q, phoneOnly, tagFilter); }} />
            <svg className="w-4 h-4 absolute left-3 top-3 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
          </div>
          <select className="input w-44" value={tagFilter}
            onChange={e => { setTagFilter(e.target.value); loadRows(0, q, phoneOnly, e.target.value); }}>
            <option value="">All tags</option>
            {tags.map(t => <option key={t.id} value={t.slug}>{t.name} ({t.contact_count})</option>)}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={phoneOnly}
                   onChange={e => { setPhoneOnly(e.target.checked); loadRows(0, q, e.target.checked, tagFilter); }} />
            Valid phone only
          </label>
          <button className="btn-ghost" onClick={() => loadRows(0, q, phoneOnly, tagFilter)} disabled={busy}>Search</button>
        </div>

      </div>

      {/* bulk action bar */}
      {selected.size > 0 && (
        <div className="card p-3 mb-4 flex flex-wrap items-center gap-3 bg-brand-50/40 border-brand-100">
          <span className="text-sm font-medium text-brand-700"><strong>{selected.size}</strong> selected</span>
          <select className="input h-9" defaultValue=""
                  onChange={e => { bulkAddTag(Number(e.target.value)); e.target.value = ''; }}>
            <option value="">+ Add tag…</option>
            {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select className="input h-9" defaultValue=""
                  onChange={e => { bulkRemoveTag(Number(e.target.value)); e.target.value = ''; }}>
            <option value="">– Remove tag…</option>
            {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <div className="flex-1" />
          <button className="btn-ghost btn-sm" onClick={clearSelection}>Clear</button>
        </div>
      )}

      {/* contacts table */}
      <div className="card overflow-hidden mb-6">
        <div className="px-5 py-3.5 flex items-center justify-between border-b border-slate-100">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">All contacts</h3>
            <p className="text-xs text-slate-500 mt-0.5">{total.toLocaleString()} match the current filters</p>
          </div>
          <div className="text-xs text-slate-500">{busy ? 'Loading…' : null}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/60 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5 w-10"><input type="checkbox" checked={allChecked} onChange={() => allChecked ? clearSelection() : selectAllOnPage()} /></th>
                <th className="px-2 py-2.5 text-left">Name</th>
                <th className="px-2 py-2.5 text-left">Phone</th>
                <th className="px-2 py-2.5 text-left">Email</th>
                <th className="px-2 py-2.5 text-left">Organisation</th>
                <th className="px-2 py-2.5 text-left">Tags</th>
                <th className="px-2 py-2.5 text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.id} className={`hover:bg-slate-50/60 ${!r.phone_valid ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-2.5"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} /></td>
                  <td className="px-2 py-2.5">
                    <div className="font-medium text-slate-900">{r.name || <span className="text-slate-400">—</span>}</div>
                    <div className="text-xs text-slate-500 truncate max-w-[220px]">{r.designation}{r.designation && r.city ? ' · ' : ''}{r.city}</div>
                  </td>
                  <td className="px-2 py-2.5 whitespace-nowrap text-slate-700">+{r.phone}{!r.phone_valid && <span className="ml-2 text-[10px] uppercase tracking-wider text-rose-600">invalid</span>}</td>
                  <td className="px-2 py-2.5 truncate max-w-[200px] text-slate-700">{r.email || <span className="text-slate-400">—</span>}</td>
                  <td className="px-2 py-2.5 truncate max-w-[180px] text-slate-700">{r.org_name}</td>
                  <td className="px-2 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(r.tags || []).map(t => <TagPill key={t.id} tag={t} />)}
                    </div>
                  </td>
                  <td className="px-2 py-2.5 pr-4 text-right whitespace-nowrap">
                    <button className="btn-ghost btn-sm text-rose-600 border-rose-100 hover:bg-rose-50" onClick={() => deleteContact(r.id, r.name, r.source)}>Delete</button>
                  </td>
                </tr>
              ))}
              {!rows.length && !busy && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No contacts match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>{offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}</span>
          <div className="flex gap-2">
            <button className="btn-ghost btn-sm" disabled={offset === 0 || busy} onClick={() => loadRows(Math.max(0, offset - limit), q, phoneOnly, tagFilter)}>← Previous</button>
            <button className="btn-ghost btn-sm" disabled={offset + limit >= total || busy} onClick={() => loadRows(offset + limit, q, phoneOnly, tagFilter)}>Next →</button>
          </div>
        </div>
      </div>

      {/* activity */}
      <div className="card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Recent activity</h3>
            <p className="text-xs text-slate-500 mt-0.5">Sends and webhook events. Refreshes every 5s.</p>
          </div>
          <button className="btn-ghost btn-sm" onClick={onLoadActivity}>Refresh</button>
        </div>
        <ActivityList sends={activity.sends} events={activity.events} />
      </div>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="card p-5">
      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function RowFeedback({ id, linkFor, sendFor }) {
  if (linkFor?.id === id) {
    if (linkFor.loading) return <div className="text-[11px] text-slate-400 mt-1">…</div>;
    if (linkFor.url) return <a href={linkFor.url} target="_blank" rel="noreferrer" className="block text-[11px] text-brand-600 mt-1">open ↗ (copied)</a>;
    if (linkFor.error) return <div className="text-[11px] text-rose-600 mt-1">{linkFor.error}</div>;
  }
  if (sendFor?.id === id) {
    if (sendFor.loading) return <div className="text-[11px] text-slate-400 mt-1">{sendFor.channel}: sending…</div>;
    if (sendFor.error) return <div className="text-[11px] text-rose-600 mt-1">{sendFor.channel}: {sendFor.error}</div>;
    if (sendFor.result) return <div className={`text-[11px] mt-1 ${sendFor.result.dry_run ? 'text-slate-500' : 'text-emerald-600'}`}>{sendFor.channel} {sendFor.result.dry_run ? '(dry-run)' : 'sent ✓'}</div>;
  }
  return null;
}

/* ============================================================================
 * ACTIVITY LIST
 * ============================================================================ */
function ActivityList({ sends, events }) {
  const items = [
    ...sends.map(s => ({ kind: 'send', ts: s.ts, ...s })),
    ...events.map(e => ({ kind: 'event', ts: e.ts, ...e })),
  ].sort((a, b) => (a.ts < b.ts ? 1 : -1)).slice(0, 50);
  if (!items.length) return <div className="text-sm text-slate-400 py-6 text-center">Nothing yet. Try the WA / Email actions on a contact.</div>;
  return (
    <ul className="divide-y divide-slate-100">
      {items.map(it => <ActivityRow key={`${it.kind}-${it.id}`} it={it} />)}
    </ul>
  );
}

function ActivityRow({ it }) {
  const time = fmtIST(it.ts);
  const who  = `${it.contact_name} · ${it.channel === 'wa' ? '+' + (it.contact_phone || '') : (it.contact_email || '')}`;
  const label = it.kind === 'send'
    ? `${it.channel.toUpperCase()} ${it.status}${it.provider_msg_id ? ' · ' + String(it.provider_msg_id).slice(-12) : ''}`
    : `${it.channel.toUpperCase()} ${it.event}`;
  const tone =
    it.kind === 'send' ?
      (it.status === 'sent' ? 'bg-emerald-50 text-emerald-700' :
       it.status === 'failed' ? 'bg-rose-50 text-rose-700' :
       'bg-brand-50 text-brand-700')
    : it.event === 'delivered' ? 'bg-emerald-50 text-emerald-700'
    : it.event === 'read'      ? 'bg-sky-50 text-sky-700'
    : it.event === 'failed'    ? 'bg-rose-50 text-rose-700'
    : it.event === 'reply'     ? 'bg-violet-50 text-violet-700'
    : 'bg-slate-100 text-slate-700';
  return (
    <li className="py-2 flex items-center gap-4 text-sm">
      <span className="text-xs text-slate-400 tabular-nums w-44 shrink-0">{time}</span>
      <span className={`text-[11px] font-semibold tracking-wide px-2 py-0.5 rounded ${tone} w-44 shrink-0 truncate`}>{label}</span>
      <span className="text-slate-700 truncate flex-1">{who}</span>
      {it.error && <span className="text-[11px] text-rose-600 truncate max-w-xs" title={it.error}>{it.error}</span>}
    </li>
  );
}

/* ============================================================================
 * TAGS PAGE
 * ============================================================================ */
function TagsPage({ tags, onChange }) {
  return (
    <div className="px-8 py-6 max-w-[1100px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Tags</h1>
          <p className="text-sm text-slate-500 mt-0.5">Define audiences. Campaigns send to all contacts with selected tags.</p>
        </div>
      </div>
      <div className="card p-5">
        <TagManagerInner tags={tags} onChange={onChange} />
      </div>
    </div>
  );
}

function TagManagerInner({ tags, onChange }) {
  const [name, setName]   = useState('');
  const [color, setColor] = useState(TAG_PALETTE[0].value);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');

  async function create() {
    if (!name.trim()) return;
    setBusy(true); setErr('');
    try {
      await api.post('/api/tags', { name: name.trim(), color });
      setName(''); setColor(TAG_PALETTE[0].value);
      onChange();
    } catch (e) { setErr(e.body?.error || e.message); }
    finally { setBusy(false); }
  }
  async function rename(id, current) {
    const newName = window.prompt('Rename tag', current);
    if (!newName || newName === current) return;
    try { await api.patch(`/api/tags/${id}`, { name: newName.trim() }); onChange(); }
    catch (e) { window.alert('Rename failed: ' + (e.body?.error || e.message)); }
  }
  async function recolor(id, hex) {
    try { await api.patch(`/api/tags/${id}`, { color: hex }); onChange(); }
    catch (e) { window.alert('Recolor failed: ' + (e.body?.error || e.message)); }
  }
  async function remove(id, label) {
    if (!window.confirm(`Delete tag "${label}"? Contacts keep their data — only the tag link is removed.`)) return;
    try { await api.del(`/api/tags/${id}`); onChange(); }
    catch (e) { window.alert('Delete failed: ' + (e.body?.error || e.message)); }
  }

  return (
    <>
      <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50 mb-5">
        <div className="text-sm font-semibold text-slate-900 mb-2">Create a new tag</div>
        <div className="flex flex-wrap items-center gap-3">
          <input className="input flex-1 min-w-[220px]" placeholder="Tag name (e.g. Manufacturers, VIP exhibitors)"
                 value={name} onChange={e => setName(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter') create(); }} />
          <ColorPicker value={color} onChange={setColor} />
          <button className="btn" onClick={create} disabled={busy || !name.trim()}>Create tag</button>
        </div>
        {err && <div className="mt-2 text-sm text-rose-600">{err}</div>}
      </div>

      <div className="space-y-2">
        {tags.map(t => (
          <div key={t.id} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition">
            <TagPill tag={t} size="lg" />
            <div className="flex-1" />
            <span className="text-xs text-slate-500">{t.contact_count.toLocaleString()} contacts</span>
            {t.is_system && <span className="text-[10px] uppercase tracking-wider text-slate-400">system</span>}
            <ColorPicker value={t.color} onChange={(hex) => recolor(t.id, hex)} compact />
            <button className="btn-ghost btn-sm" onClick={() => rename(t.id, t.name)}>Rename</button>
            {!t.is_system && <button className="btn-ghost btn-sm text-rose-600 border-rose-100 hover:bg-rose-50" onClick={() => remove(t.id, t.name)}>Delete</button>}
          </div>
        ))}
        {!tags.length && <div className="text-sm text-slate-400 text-center py-6">No tags yet.</div>}
      </div>
    </>
  );
}

function ColorPicker({ value, onChange, compact = false }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 ${compact ? 'h-8 px-2' : 'h-10 px-3'} rounded-lg border border-slate-200 bg-white text-sm hover:border-slate-300`}
      >
        <span className="w-4 h-4 rounded-full" style={{ background: value }} />
        {!compact && <span className="text-slate-700">Color</span>}
        <svg className="w-3 h-3 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
      </button>
      {open && (
        <div className="absolute z-10 right-0 mt-2 p-2 bg-white rounded-xl border border-slate-200 shadow-soft grid grid-cols-7 gap-1.5">
          {TAG_PALETTE.map(c => (
            <button
              key={c.value}
              type="button"
              onClick={() => { onChange(c.value); setOpen(false); }}
              className={`w-7 h-7 rounded-full border-2 ${value === c.value ? 'border-slate-900' : 'border-transparent'} hover:scale-110 transition`}
              style={{ background: c.value }}
              title={c.name}
            />
          ))}
        </div>
      )}
    </div>
  );
}
